/**
 * Implement Gatsby's Browser APIs in this file.
 *
 * See: https://www.gatsbyjs.org/docs/browser-apis/
 */

// You can delete this file if you're not using it
import './src/global.css'
import '@fontsource/open-sans/latin-400.css'
import '@fontsource/open-sans/latin-500.css'
import '@fontsource/open-sans/latin-600.css'
import '@fontsource/open-sans/latin-700.css'
import {
    getLanguageSwitchPath,
    isZhCNByRoute,
    type LocaleCode,
} from './src/utils/locale'

const PREFERRED_LANG_STORAGE_KEY = 'elisi-preferred-lang'
const EXPLICIT_ENGLISH_ROUTE_PATHS = new Set([
    '/',
    '/home/',
    '/download/',
    '/dock/',
    '/pricing/',
    '/features/',
    '/about/',
    '/privacy/',
    '/features/goal/',
    '/features/habits/',
    '/features/notes/',
    '/features/planner/',
    '/features/ai-assistant/',
    '/features/cross-platforms/',
    '/features/overview/',
    '/features/sync/',
    '/features/tree-structure/',
])

const normalizePathname = (pathname?: string) => {
    if (!pathname) {
        return undefined
    }

    const normalized = pathname.replace(/^\/+|\/+$/g, '')

    return normalized ? `/${normalized}/` : '/'
}

const getLocaleFromPathname = (pathname?: string) => {
    const normalizedPathname = normalizePathname(pathname)

    if (!normalizedPathname) {
        return undefined
    }

    if (normalizedPathname.includes('-zh/')) {
        return 'zh-cn'
    }

    if (EXPLICIT_ENGLISH_ROUTE_PATHS.has(normalizedPathname)) {
        return 'en-us'
    }

    return undefined
}

const getLocaleFromSearch = (search?: string): LocaleCode | undefined => {
    if (!search || typeof URLSearchParams === 'undefined') {
        return undefined
    }

    const lang = new URLSearchParams(search.replace(/^\?/, '')).get('lang')?.toLowerCase()

    if (lang === 'zh-cn' || lang === 'en-us') {
        return lang
    }

    return undefined
}

const getStoredPreferredLocale = (): LocaleCode | undefined => {
    if (typeof window === 'undefined') {
        return undefined
    }

    const preferredLang = window.localStorage.getItem(PREFERRED_LANG_STORAGE_KEY)

    if (preferredLang === 'zh-cn' || preferredLang === 'en-us') {
        return preferredLang
    }

    return undefined
}

const getBrowserPreferredLocale = (): LocaleCode => {
    if (typeof navigator === 'undefined') {
        return 'en-us'
    }

    return navigator.language?.toLowerCase().startsWith('zh') ? 'zh-cn' : 'en-us'
}

const getPreferredLocale = (): LocaleCode => {
    const localeFromSearch = getLocaleFromSearch(window.location.search)

    if (localeFromSearch) {
        window.localStorage.setItem(PREFERRED_LANG_STORAGE_KEY, localeFromSearch)
        return localeFromSearch
    }

    const storedLocale = getStoredPreferredLocale()

    if (storedLocale) {
        return storedLocale
    }

    return getBrowserPreferredLocale()
}

const redirectToPreferredLocale = () => {
    if (typeof window === 'undefined') {
        return false
    }

    const targetLocale = getPreferredLocale()

    if (targetLocale !== 'zh-cn') {
        return false
    }

    const targetPath = getLanguageSwitchPath({
        pathname: window.location.pathname,
        search: window.location.search,
        hash: window.location.hash,
        targetLocale,
    })
    const targetUrl = new URL(targetPath, window.location.origin)
    const currentPathname = normalizePathname(window.location.pathname)
    const targetPathname = normalizePathname(targetUrl.pathname)

    if (!currentPathname || !targetPathname || currentPathname === targetPathname) {
        return false
    }

    const currentUrl = `${window.location.pathname}${window.location.search}${window.location.hash}`
    const nextUrl = `${targetUrl.pathname}${targetUrl.search}${targetUrl.hash}`

    if (currentUrl === nextUrl) {
        return false
    }

    window.location.replace(nextUrl)

    return true
}

const updateDocumentLocaleClass = () => {
    if (typeof window === 'undefined') {
        return
    }

    const isZhLang = isZhCNByRoute(window.location.pathname)
    window.document.documentElement.classList.remove('elisi-lang-zh-cn', 'elisi-lang-en-us')
    window.document.documentElement.classList.add(`elisi-lang-${isZhLang ? 'zh-cn' : 'en-us'}`)
}

export const onClientEntry = () => {
    const isBrowser = typeof window !== 'undefined'

    function getOS() {
        const userAgent = window.navigator.userAgent
        const platform = window.navigator.platform
        const macPlatforms = ['Macintosh', 'MacIntel', 'MacPPC', 'Mac68K']
        const windowPlatforms = ['Win32', 'Win64', 'Windows', 'WinCE']
        const iosPlatforms = ['iPhone', 'iPad', 'iPod']

        if (macPlatforms.indexOf(platform) !== -1) {
            return 'MacOS'
        } else if (iosPlatforms.indexOf(platform) !== -1) {
            return 'iOS'
        } else if (windowPlatforms.indexOf(platform) !== -1) {
            return 'Windows'
        } else if (/Android/.test(userAgent)) {
            return 'Android'
        } else if (/Linux/.test(platform)) {
            return 'Linux'
        } else {
            return 'Unknown'
        }
    }

    function isZhCN() {
        const localeFromPathname = getLocaleFromPathname(window.location.pathname)
        if (localeFromPathname) {
            localStorage.setItem(PREFERRED_LANG_STORAGE_KEY, localeFromPathname)
            return localeFromPathname === 'zh-cn'
        }

        const specifiedZhCn = (typeof URLSearchParams !== 'undefined' && new URLSearchParams(window.location.search).get('lang')?.toLocaleLowerCase() === 'zh-cn')
        const specifiedEnUs = (typeof URLSearchParams !== 'undefined' && new URLSearchParams(window.location.search).get('lang')?.toLocaleLowerCase() === 'en-us')

        if (specifiedZhCn) {
            localStorage.setItem(PREFERRED_LANG_STORAGE_KEY, 'zh-cn')
            return true
        } else if (specifiedEnUs) {
            localStorage.setItem(PREFERRED_LANG_STORAGE_KEY, 'en-us')
            return false
        }

        const preferredLang = localStorage.getItem(PREFERRED_LANG_STORAGE_KEY)
        if (preferredLang === 'zh-cn' || preferredLang === 'en-us') {
            return preferredLang === 'zh-cn'
        }

        return navigator.language.toLowerCase().startsWith('zh')
    }

    if (isBrowser) {
        const os = getOS()
        const desktopPlatforms = ['Windows', 'MacOS', 'Linux']
        window.document.documentElement.className += ' elisi-platform-' + (desktopPlatforms.includes(os) ? 'Desktop' : 'Mobile')
        window.document.documentElement.className += ' elisi-os-' + os
    }

    // @ts-expect-error:: inject global variables
    window.getOS = getOS
    // @ts-expect-error:: inject global variables
    window.isZhCN = isZhCN

    if (isBrowser) {
        void (async () => {
            if (redirectToPreferredLocale()) {
                return
            }

            updateDocumentLocaleClass()
        })()
    }

    if (isBrowser) {
        const href = window.location.href
        const search = window.location.search
        const hash = window.location.hash
        const params = new URLSearchParams((search && search.length > 1 ? search.substring(1) : '') || (hash && hash.startsWith('#') ? hash.substring(1) : ''))

        const sourceFrom = params.get('source_from')?.toLowerCase() || ''
        const sourceCampaign = params.get('source_campaign') || ''
        const sourceAdgroup = params.get('source_adgroup') || ''

        const copyWithExecCommand = (text: string) => {
            try {
                const textarea = document.createElement('textarea')
                textarea.value = text
                textarea.style.position = 'fixed'
                textarea.style.left = '-9999px'
                document.body.appendChild(textarea)
                textarea.focus()
                textarea.select()
                const successful = document.execCommand('copy')
                document.body.removeChild(textarea)
                return successful
            } catch (e) {
                return false
            }
        }

        const attemptCopy = async (text: string) => {
            if (navigator && navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
                try {
                    await navigator.clipboard.writeText(text)
                    return true
                } catch (e) {
                    // fallthrough to legacy method
                }
            }
            return copyWithExecCommand(text)
        }

        const storedRef = localStorage.getItem('ref')

        if (storedRef) {
            const tryImmediate = () => attemptCopy(storedRef)

            tryImmediate().then((ok) => {
                if (ok) return
                const onFirstInteraction = () => {
                    attemptCopy(storedRef).finally(() => {
                        window.removeEventListener('click', onFirstInteraction, true)
                        window.removeEventListener('touchstart', onFirstInteraction, true)
                        window.removeEventListener('keydown', onFirstInteraction, true)
                    })
                }
                window.addEventListener('click', onFirstInteraction, true)
                window.addEventListener('touchstart', onFirstInteraction, true)
                window.addEventListener('keydown', onFirstInteraction, true)
            })
        } else if (sourceFrom) {
            const deeplinkBase = `elisiapp://source?from=${sourceFrom}`
            const deeplink = `${deeplinkBase}${sourceCampaign ? `&campaign=${encodeURIComponent(sourceCampaign)}` : ''}${sourceAdgroup ? `&adgroup=${encodeURIComponent(sourceAdgroup)}` : ''}`

            // write to local storage
            localStorage.setItem('ref', deeplink)

            const tryImmediate = () => attemptCopy(deeplink)

            tryImmediate().then((ok) => {
                if (ok) return
                const onFirstInteraction = () => {
                    attemptCopy(deeplink).finally(() => {
                        window.removeEventListener('click', onFirstInteraction, true)
                        window.removeEventListener('touchstart', onFirstInteraction, true)
                        window.removeEventListener('keydown', onFirstInteraction, true)
                    })
                }
                window.addEventListener('click', onFirstInteraction, true)
                window.addEventListener('touchstart', onFirstInteraction, true)
                window.addEventListener('keydown', onFirstInteraction, true)
            })
        } else if (/source_from=meta/.test(href)) {
            // Fallback for malformed URLs without '?' but containing key-value pairs in path
            try {
                const queryLike = href.split('?')[1] || href.split('/').pop() || ''
                const qp = new URLSearchParams(queryLike)
                const campaign = qp.get('source_campaign') || ''
                const adgroup = qp.get('source_adgroup') || ''
                const deeplink = `elisiapp://source?from=meta${campaign ? `&campaign=${encodeURIComponent(campaign)}` : ''}${adgroup ? `&adgroup=${encodeURIComponent(adgroup)}` : ''}`
                if (deeplink) {
                    const tryImmediate = () => attemptCopy(deeplink)
                    tryImmediate()
                }
            } catch (e) {
                // ignore
            }
        }
    }
}

export const onRouteUpdate = () => {
    void (async () => {
        if (redirectToPreferredLocale()) {
            return
        }

        updateDocumentLocaleClass()
    })()
}

export const registerServiceWorker = () => true
